import { createSlice } from "@reduxjs/toolkit"
import { CampaignDetail } from "types/campaign"
import { CampaignApplication } from "types/campaign/applications"
import { Influencer } from "types/influencer"

export interface UIComponents {
    influencerPostModal: {
        postId: string,
        influencer: Influencer,
        status?: 'pending' | 'approved' | 'rejected' | 'processing',
        milestoneStep?: string | null,
        preventAction?: boolean
    } | null,
    paymentsOffcanvas: boolean,
    applicationOffcanvas: {
        campaignId: string,
        influencerId: string,
        socialAccountId?: string,
        status: 'pending' | 'accepted' | 'rejected' | 'processing'
        approve?: () => void,
        reject?: () => void
    } | null,
    postPerformanceOffcanvas: {
        collaborationId: string,
    } | null,
    error: {
        code: number,
        message: string
    } | null,
    header: {
        campaignData: CampaignDetail | null,
        detailPage?: boolean
    } | null,
    lastActiveCampaignTab: 'applications' | 'collaborations' | 'insights',
    campaignsLocation: {pathname: string, search?: string},
    notificationPreferences: boolean,
    approveApplicationCostModal: {
        application: CampaignApplication;
        isOpen: boolean;
    } | null,
}

const initialState: UIComponents = {
    influencerPostModal : null,
    paymentsOffcanvas: false,
    error: null,
    header: {
        campaignData: null,
        detailPage: false
    },
    applicationOffcanvas: null,
    postPerformanceOffcanvas: null,
    lastActiveCampaignTab: 'applications',
    campaignsLocation: {pathname: '/campaign'},
    notificationPreferences: false,
    approveApplicationCostModal: null,
}

const uiSlice = createSlice({
    name: 'ui',
    initialState,
    reducers: {
        showInfluencerPostModal: (state: UIComponents, action: { payload: { postId: string, influencer: Influencer, status?: 'pending' | 'approved' | 'rejected' | 'processing', milestoneStep?: string | null, preventAction?: boolean } }) => {
                state.influencerPostModal = action.payload;
            },
        closeInfluencerPostModal: (state: UIComponents) => {
            state.influencerPostModal = null;
        },
        togglePaymentsOffcanvas: (state: UIComponents, action : {payload: boolean}) => {
            state.paymentsOffcanvas = action.payload;
        },
        setError: (state, action) => {
            state.error = action.payload;
        },
        setHeaderCampaignData: (state, {payload}) => {
            const {campaign, detailPage} = payload;
            state.header = {
                ...(state.header ?? {}),
                campaignData: campaign,
                detailPage
            }
        },
        toggleApplicationOffcanvas: (state, action : {payload: {campaignId: string, influencerId: string, socialAccountId: string, status: 'pending' | 'accepted' | 'rejected' | 'processing', approve?: () => void, reject?: () => void} | null}) => {
            state.applicationOffcanvas = action.payload;
        },
        togglePostPerformanceOffcanvas: (state, action: {payload: {collaborationId: string} | null}) => {
            state.postPerformanceOffcanvas = action.payload
        },
        setLastActiveCampaignTab : (state, action: {payload: 'applications' | 'collaborations' | 'insights'}) => {
            state.lastActiveCampaignTab = action.payload;
        }, 
        setCampaignsLocation: (state, action: {payload: {pathname: string, search?: string}}) => {
            state.campaignsLocation = action.payload
        },
        toggleNotificationPreferencesModal: (state, {payload}) => {
            state.notificationPreferences = payload;
        },
        toggleApproveApplicationCostModal: (state, {payload}: {payload: {application: CampaignApplication; isOpen: boolean} | null}) => {
            state.approveApplicationCostModal = payload;
        }
    }
})

export const {
    showInfluencerPostModal,
    closeInfluencerPostModal,
    togglePaymentsOffcanvas,
    setError,
    setHeaderCampaignData,
    toggleApplicationOffcanvas,
    togglePostPerformanceOffcanvas,
    setCampaignsLocation,
    setLastActiveCampaignTab,
    toggleNotificationPreferencesModal,
    toggleApproveApplicationCostModal
} = uiSlice.actions;

export default uiSlice.reducer